// Shared types + constants for shop configuration. Safe to import from
// both server (API routes) and client (hooks, components) — no server-only
// dependencies (pg, etc.) live here.

export interface ShopConfigPF { id: string; label: string; type: "pct" | "flat"; amount: string; flatAmount?: string; }

// Arbitrary merchant-defined questions on the customer /request form.
// Replaces the SW-shaped "facility type" + "services" hardcodes — a
// furniture shop adds "Room type"; an audio shop adds "Primary use";
// an industrial supplier adds nothing at all. Submitted values land in
// quote_requests.services.custom_fields as { [fieldId]: string | string[] }.
//
// The CONTROL implies the CARDINALITY. There is deliberately no
// `multiple` / `max_selections` flag: a checkbox that silently unchecks
// its neighbor lies to the customer. Pick the control that can express
// the question and the stored shape follows from it.
//
//   dropdown | radio    → stores a string   (one option id)
//   checkbox           → stores a string[] (option ids)
//   text | textarea    → stores a string
//   file               → stores NOTHING in custom_fields
//
// Option IDS are the stored value, never labels, so renaming an option
// never rewrites history.
//
// `file` is the one type whose answer does NOT live in the values map: a
// file answer is a row in `quote_request_files` tagged with this field's
// id. Mirroring the filename into `services.custom_fields` would create a
// second source of truth for the same fact — see isCustomFilled(), which
// takes the attachment list instead.
export type CustomFieldType =
  | "dropdown"      // single choice, collapsed <select>
  | "radio"         // single choice, expanded cards — browser enforces exclusivity
  | "checkbox"      // multiple choice, expanded cards
  | "text"          // single line
  | "textarea"      // multi-line
  | "file";         // one named document ("Upload your water report") — no options

/** Pre-`custom-field-controls-and-depth` type names. Still present in
 *  every `shop_config` row that predates this lane and NEVER rewritten —
 *  there is no migration. They are mapped to canonical values on the READ
 *  path (see `canonicalCustomFieldType`), so a shop that never opens the
 *  designer again keeps rendering exactly as it did. Read support for
 *  these is permanent, not transitional. */
export type LegacyCustomFieldType = "select" | "multiselect";

const LEGACY_CUSTOM_FIELD_TYPES: Record<LegacyCustomFieldType, CustomFieldType> = {
  select: "dropdown",
  multiselect: "checkbox",
};

const CANONICAL_CUSTOM_FIELD_TYPES: readonly CustomFieldType[] = [
  "dropdown", "radio", "checkbox", "text", "textarea", "file",
];

/** The ONE place legacy type names are resolved. Every renderer, validator
 *  and display resolver must funnel through this (or the predicates below)
 *  rather than comparing `field.type` to a string literal — a site that
 *  forgets `"select"` renders an empty `<div>` where a required question
 *  should be, on a live storefront.
 *
 *  Returns `null` for an unknown type: the field is dropped, not guessed. */
export function canonicalCustomFieldType(type: unknown): CustomFieldType | null {
  if (typeof type !== "string") return null;
  if ((CANONICAL_CUSTOM_FIELD_TYPES as readonly string[]).includes(type)) return type as CustomFieldType;
  return LEGACY_CUSTOM_FIELD_TYPES[type as LegacyCustomFieldType] ?? null;
}

/** Single-choice — the answer is a string (an option id). `dropdown`,
 *  `radio`, and legacy `select`. */
export function isSingleChoiceField(type: unknown): boolean {
  const t = canonicalCustomFieldType(type);
  return t === "dropdown" || t === "radio";
}

/** Multiple-choice — the answer is a string[]. `checkbox` and legacy
 *  `multiselect`. */
export function isMultiChoiceField(type: unknown): boolean {
  return canonicalCustomFieldType(type) === "checkbox";
}

/** Any control backed by `options[]`, regardless of cardinality. Used by
 *  the designer to decide whether to show the options editor and whether a
 *  field can gate a `show_when` rule. `file` is NEITHER single- nor
 *  multi-choice, so it is never a choice field. */
export function isChoiceField(type: unknown): boolean {
  return isSingleChoiceField(type) || isMultiChoiceField(type);
}

/** A per-question document upload. Its answer lives in
 *  `quote_request_files`, not in the submitted values map, so every
 *  "is this answered?" / "render this answer" path has to special-case it
 *  rather than reading `values[field.id]`. */
export function isFileField(type: unknown): boolean {
  return canonicalCustomFieldType(type) === "file";
}

export type CustomFieldStep = "about" | "finish"; // where in the wizard the field renders
export interface CustomFieldOption { id: string; label: string }

/** The quick form's single optional binary (Yes/No) question (kickoff:
 *  quick-form-binary-field.md). Deliberately NOT a CustomField — hard-
 *  capped at exactly one, exactly two options, no select/checkbox shape.
 *  A merchant needing more than this escalates to the multi-step's full
 *  `custom_fields`. The stored answer is always the STABLE key "yes" /
 *  "no" (never true_label/false_label) — see normalizeQuickFormCustomField
 *  and resolveQuickForm(). */
export interface QuickFormCustomField {
  /** Master gate. Default FALSE — nothing renders on any storefront
   *  until a merchant turns this on. */
  enabled?: boolean;
  /** Stable slug (^[a-z0-9][a-z0-9-]*$) — the state.customValues /
   *  services.custom_fields key. Renaming this would orphan historical
   *  answers, same risk class as a CustomField id. */
  id: string;
  /** The question, e.g. "Do you have a water report?". */
  label: string;
  help_text?: string;
  /** Default FALSE — an unanswered optional question never blocks
   *  submit. */
  required?: boolean;
  /** Display label for the "true" button. Default "Yes". Purely
   *  cosmetic — renaming this does NOT change the stored "yes" key on
   *  historical or future answers. */
  true_label?: string;
  /** Display label for the "false" button. Default "No". Same
   *  cosmetic-only guarantee as true_label. */
  false_label?: string;
}
export interface CustomField {
  id: string;
  label: string;
  helpText?: string;
  type: CustomFieldType;
  step: CustomFieldStep;
  required?: boolean;
  options?: CustomFieldOption[];   // dropdown / radio / checkbox only

  // ── `file` fields only ──────────────────────────────────────────────
  // Per-question upload limits, layered over the shop's `file_upload`
  // block by resolveUploadRules() (src/lib/file-upload.ts) — the ONE
  // function that decides an upload's effective rules, for both the
  // token endpoint and the submit route. Never read these directly to
  // authorize an upload.
  /** Max attachments answering THIS question. Unset ⇒ 1: it's a
   *  question, not a folder. (The shop-level `max_files` still caps the
   *  generic catch-all zone.) */
  max_files?: number;
  /** Unset ⇒ the shop's `file_upload.max_file_size_mb`. */
  max_file_size_mb?: number;
  /** Unset ⇒ the shop's `file_upload.allowed_types`. */
  allowed_types?: string[];

  /** Position within the field's resolved step, sorted ascending.
   *  Custom fields default to 100 + their custom_fields[] array
   *  index so they fall AFTER built-ins (whose defaults come from
   *  BUILTIN_FIELD_ROWS order). The unified FieldsDesigner assigns
   *  fresh display_order values on drag to interleave built-ins +
   *  custom fields. */
  display_order?: number;
  /** Per-field width on the customer form. "half" packs greedily
   *  with the previous half-width row into a 2-col grid; "full" (the
   *  default when unset) renders edge-to-edge. A trailing odd half
   *  falls back to full-width so a single half-field on its own line
   *  doesn't leave a visually empty column. Mobile (<600px) drops
   *  pair-mode entirely. */
  column_width?: "full" | "half" | "third";
  /** Conditional visibility — when set, the field renders only when
   *  the referenced earlier field's value matches the operator/value
   *  combination. Cycles are prevented at config time by limiting the
   *  designer's dependency picker to fields BEFORE this one in the
   *  unified order. See isFieldVisible() in form-field-order.ts. */
  show_when?: ShowWhenRule;
}

// Pre-built packages a merchant can offer at the top of the customer's
// build-your-quote step. Customer clicks "Add to Quote" → all line items
// drop into their cart. They can edit qty / remove individual items after.
// Stored in shop_config.bundles JSONB; never includes cost or any internal
// data — line items are { variantId, qty } only.
export interface BundleLineItem {
  variantId: string;
  qty: number;
}
export interface Bundle {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  lineItems: BundleLineItem[];
  isActive: boolean;
}

// What the customer is asked to do when they hit this terms option.
// "pay_online"   — link out to Shopify checkout (existing default)
// "accept_terms" — accept on terms (Net 30, etc); optional PO# capture
// "instructions" — read + acknowledge instructions (wire/ACH where they pay separately)
// "deposit"      — pay a percentage upfront (v1 routes to the full invoice url; see CTA logic)
export type PaymentTermsActionType = "pay_online" | "accept_terms" | "instructions" | "deposit";

// Shopify shop policy types Shopify's `shop.shopPolicies` returns.
// We mirror the enum verbatim so the type field round-trips intact;
// adding new variants on Shopify's side just means an unknown
// string flows through (we don't validate aggressively at read time).
export type ShopifyPolicyType =
  | "REFUND_POLICY"
  | "SHIPPING_POLICY"
  | "PRIVACY_POLICY"
  | "TERMS_OF_SERVICE"
  | "SUBSCRIPTION_POLICY";

export interface ShopifyPolicy {
  type: ShopifyPolicyType;
  title: string;
  url: string;
  /** ISO 8601 stamp written when the row was last refreshed from
   *  Shopify. Read by the admin UI to render "Last synced: 2h ago". */
  last_synced_at: string;
  /** Merchant toggle — when true the link renders on customer quote
   *  pages. Default `true` on first sync so a freshly-installed
   *  merchant gets their policies surfaced immediately; merchant
   *  can flip off later via /settings/settings → Quotes. The sync
   *  helper PRESERVES this across re-syncs (never resets to true
   *  on update). */
  enabled: boolean;
}

export interface PaymentTermsOption {
  id: string;
  label: string;        // e.g. "Net 30", "50/50 split", "Payment on acceptance"
  body: string;         // multiline body printed on the quote/PDF
  isDefault?: boolean;

  // Required going forward; legacy rows without it are treated as "pay_online"
  // by readers (see shop-config.ts normalization on load).
  action_type?: PaymentTermsActionType;

  // For action_type === "accept_terms"
  requires_po?: boolean;
  accept_button_label?: string;        // defaults to "Accept on Terms"

  /** Require the customer to upload a signed PO document before they
   *  can accept on terms. Stored in quote_files (context='accept_po')
   *  + referenced in the accepted_terms event payload. When unset /
   *  false, the upload widget doesn't render. */
  requires_po_file?: boolean;
  /** Label above the upload widget. Defaults to "Upload signed PO". */
  po_file_label?: string;
  /** Optional helper text beneath the widget (e.g. "PDF preferred,
   *  10 MB max"). */
  po_file_helper?: string;

  /** Require the rep to acknowledge a "I've confirmed this customer is
   *  approved for {label}" checkbox in the pre-send confirm modal
   *  before the email can actually fire. Use this for terms with
   *  manager-level financial exposure (Net 30, large deposits, custom
   *  agreements) where a rep accidentally sending should be a hard
   *  stop, not a soft warning. Applies to ANY action_type — Net 30
   *  isn't the only thing worth gating. */
  requires_confirmation?: boolean;

  // For action_type === "deposit"
  deposit_percent?: number;             // e.g. 50

  // For action_type === "instructions"
  acknowledge_button_label?: string;    // defaults to "I'll initiate the transfer"

  // Future hook — Shopify B2B (Plus) integration.
  // When set on a Plus store, the draft order push includes
  //   draft_order.payment_terms = { payment_terms_template_id: <this id> }
  // so Shopify carries the term natively. Null/blank for non-Plus
  // merchants OR Plus merchants who haven't mapped this option yet.
  // No runtime behavior is gated on this in v1 — it just rides along.
  shopify_payment_terms_template_id?: string | null;

  /** Optional external payment portal URL (Bill.com, Stripe ACH,
   *  Plaid, CRM ACH link, etc.). Renders as a button on the
   *  customer-facing quote page. The customer enters payment data
   *  on the merchant's portal — QuoteSense never sees or stores
   *  account numbers. Validated via isValidPaymentUrl() at render
   *  time (https?: only — rejects javascript:/data:/etc.). Empty
   *  string treated as absent.
   *
   *  Renders in renderPayOnline + renderDeposit as a SECONDARY
   *  outlined CTA alongside the Shopify checkout button; in
   *  renderInstructions it replaces the acknowledge button as the
   *  PRIMARY CTA (click is implicit acknowledgement). Never renders
   *  in renderAcceptTerms (that mode is explicitly "no payment now"). */
  payment_link_url?: string;
  /** Button copy for the payment-link CTA. Defaults to
   *  "Pay via portal" at render time when blank. */
  payment_link_label?: string;
}
export interface ShopConfigVendorFee { id: string; type: "flat" | "pct"; label: string; amount: string; threshold: string; thresholdDir: "always" | "under" | "over"; }
export interface ShopConfigVendorPreset {
  id: string;
  name: string;
  aliases: string[];
  defaultFreight: string;
  // See FreightType in quote-types.ts. "threshold" / "minimum_at_threshold"
  // pair with freeFreightAtOrAbove; others ignore it.
  freightType: "manual" | "free" | "per_sku" | "threshold" | "minimum_at_threshold";
  freightPerSku: string;
  freeFreightAtOrAbove: string;
  fees: ShopConfigVendorFee[];
}

/** Product-page "Request a Quote" button — AUTO-PLACE via the app
 *  embed's boot(). The cooperative path is the QuoteSense app block
 *  next to Add-to-Cart (theme editor); this is the universal fallback
 *  for LOCKED themes (e.g. self-contained ATC + wishlist blocks that
 *  can't nest a block) where JS injection is the only option. Every
 *  field is optional + additive; the whole feature is OFF unless
 *  auto_place_enabled === true, so an unset config renders storefronts
 *  byte-identically. The embed reads this off PublicShopConfig and
 *  runs the injector in src/embed/product-button.ts. Phase-2 polish
 *  (sibling-shape matching, replace-existing, per-product exclude_tags)
 *  is intentionally out of scope here. */
export interface ProductButtonConfig {
  /** Master gate. Default false (opt-in) — nothing injects until a
   *  merchant flips this on AND the App Embed is enabled storewide. */
  auto_place_enabled?: boolean;
  /** Button copy. Default "Request a quote". */
  label?: string;
  /** Button background. Default: brand accent_color. */
  bg_color?: string;
  /** Button text colour. Default "#ffffff". */
  text_color?: string;
  /** Form the button opens. "quick" = single-screen prefilled quick
   *  form; "full" = the 4-step wizard. Default "quick". */
  open_mode?: "quick" | "full";
  /** Overlay style the form opens in. Default "drawer". */
  display_mode?: "drawer" | "modal";
  /** "form" (default) opens the request form with this product
   *  prefilled — today's behavior, unchanged. "add_to_quote" stages
   *  the product into the standalone Quote Basket instead (requires
   *  `ShopConfig.basket.enabled`); when basket is off, the embed
   *  falls back to the form path. Mirrors the embed's ProductButtonConfig
   *  in src/embed/product-button.ts. */
  mode?: "form" | "add_to_quote";
  /** Which form the auto-placed button opens (docs/MULTI_FORM.md §6).
   *  Unset/blank = the shop's default form. Only applies when
   *  `mode === "form"` — `mode === "add_to_quote"` stages into the Quote
   *  Basket without opening a form, so `form_id` is irrelevant there.
   *  Set via the real "Which form" dropdown on
   *  /settings/embed/product-button (auto-place is configured inside the
   *  app, so it can list the merchant's forms by name — unlike the theme
   *  app block, which can only take a typed Form ID since Shopify block
   *  settings are static Liquid). Validated at save time
   *  (normalizeProductButton): must be slug-shaped (^[a-z0-9][a-z0-9-]*$);
   *  a malformed value is dropped rather than persisted. */
  form_id?: string;
  /** Advanced escape hatch — a custom CSS selector for the injection
   *  anchor, overriding the built-in add-to-cart chain. Queried
   *  against the whole document (not scoped to the form). When set but
   *  not found, the injector falls back to appending at the end of the
   *  product form. Default unset = built-in chain. */
  target_selector?: string;
  /** Insert the button before or after the resolved anchor. Default
   *  "after". Ignored on the end-of-form fallback (always appended). */
  position?: "after" | "before";
  /** Styling axis — mirrors the app block's `button_style`. "color"
   *  (default) uses bg_color/text_color + shape/font below, exactly
   *  like today. "outlined" is the inverted twin — transparent
   *  background, bg_color used as the border + text colour instead —
   *  same shape/font axes. "theme_button" clones the resolved anchor's
   *  own CSS classes onto the button and drops all inline chrome so the
   *  theme's native button styling applies (the from-scratch pill
   *  never matches a theme's full-width/rectangular buttons).
   *  "theme_inverted" ("Match theme button — inverted") also clones the
   *  anchor's classes (same size/shape/font) but inline-overrides
   *  colour to the anchor's OWN colour flipped filled<->outlined —
   *  degrades to "theme_button" when the anchor's colours can't be
   *  read. "custom_class" applies button_class instead of cloning. */
  style_mode?: "color" | "outlined" | "theme_button" | "theme_inverted" | "custom_class";
  /** Space-separated theme CSS class(es) applied when style_mode is
   *  "custom_class", e.g. "button button--primary". Ignored otherwise. */
  button_class?: string;
  /** Button width. "auto" (default) = inline-block, content width.
   *  "full" = block, 100% width — matches full-width theme buttons. */
  width?: "full" | "auto";
  /** Corner radius + padding. "rounded" (default) reproduces the
   *  original hardcoded pill (8px radius, 12px 20px padding). "pill" /
   *  "square" are alternate presets; "inherit" drops border-radius +
   *  padding so a theme_button/custom_class's own CSS controls both. */
  shape?: "rounded" | "pill" | "square" | "inherit";
  /** Font family axis. "inherit" (default) reproduces today's exact
   *  font declarations (family: inherit, weight 600, size 15px, line-
   *  height 1.2) — back-compat. "system" swaps in an explicit system
   *  sans-serif stack (same weight/size/line-height) for a page-
   *  independent look. Dropped entirely under style_mode "theme_button"
   *  / "custom_class" (the cloned/custom classes own typography). */
  font_mode?: "inherit" | "system";
}

/** Standalone Quote Basket (P1-core embed feature). OFF unless
 *  `enabled === true`. When on, the embed unifies quote ITEMS onto the
 *  basket store (the single source of truth across every surface), lets
 *  `product_button.mode="add_to_quote"` stage items into it, and mounts a
 *  slim side-anchored "View Quote" edge tab (shown only when the quote is
 *  non-empty) as the re-entry into the embedded multi-step form — the one
 *  review/submit surface. No standalone drawer, floating bubble, or
 *  cart-adjacent count. Server type must match the embed's shape verbatim
 *  — see ShopConfig.basket in src/embed/index.ts. */
export interface BasketConfig {
  /** Master gate. Default false. */
  enabled?: boolean;
  /** Label on the "View Quote" edge tab (may show the count, e.g.
   *  "View Quote (2)"). Default "View Quote". */
  tab_label?: string;
  /** Edge-tab background colour. Defaults to the shop's brand accent. */
  tab_color?: string;
  /** Viewport edge the tab affixes to. Default "right". */
  tab_side?: "right" | "left";
  /** Show the "View Quote" edge tab. Default true — it's the re-entry
   *  into the quote; a merchant can disable it (re-entry then falls back
   *  to the add toast's "Review quote →" link). */
  show_tab?: boolean;
}

// ── Multi-form / intake flows (docs/MULTI_FORM.md) ────────────────────────
// A FORM is a named, editable COMPOSITION (an ordered/toggleable set of
// sections + copy + field-step overrides) layered over the shop-level
// SHARED brand. Placements pick which form loads by id. Today's single-
// form config is the implicit `default` form. This block adds the schema
// surface that makes multiple forms REPRESENTABLE / RESOLVABLE /
// PERSISTABLE (Lane 1 = plumbing); Lane 2 renders them, Lane 3 edits them.
//
// The shared-vs-per-form boundary is the load-bearing decision. Its SINGLE
// documented source of truth is PER_FORM_OVERRIDE_KEYS (declared just after
// ShopConfig) — that list, plus `section_order` + `describe`, is the whole
// per-form surface. Everything else on ShopConfig (brand, appearance,
// support_email, policies, payment terms, business info, quote_validity_
// days, inventory, catalog rules, product_button, basket, …) is SHARED and
// is NEVER overridden per form. resolveForm() enforces this at runtime.

/** Section ids for a form's `section_order` (spec §4.3). This is a NEW,
 *  fixed vocabulary — distinct from the legacy FormStepsConfig step names
 *  (`about`/`shipping`/`build`/`finish`) and CustomFieldStep. Lane 2's
 *  config-driven stepDefs maps these ids onto renderers:
 *    - `contact`  ≈ the legacy `about` step (name / email / phone / …)
 *    - `shipping` — the shipping-address step (gated by require_shipping)
 *    - `products` ≈ the legacy `build` step (product search + basket).
 *                   OMITTABLE — an intake form may drop it entirely.
 *    - `describe` — the promoted free-text "what's the problem/project?"
 *                   step (see DescribeSectionConfig). Includable + leadable.
 *    - `consent`  — the consent-checkbox step.
 *    - `finish`   — the terminal submit/confirmation step. Exactly one,
 *                   always LAST.
 *  Fixed known types, reorderable/toggleable — NOT an arbitrary section
 *  graph (spec §8.2; the open graph is gated path b). */
export type FormSectionId =
  | "contact" | "shipping" | "products" | "describe" | "consent" | "finish";

/** The canonical set of known section ids. normalizeSectionOrder() validates
 *  against this; Lane 2 reads it to build stepDefs. */
export const FORM_SECTION_IDS: readonly FormSectionId[] = [
  "contact", "shipping", "products", "describe", "consent", "finish",
] as const;

/** The terminal section — exactly one, always last in a section_order. */
export const FORM_SECTION_TERMINAL: FormSectionId = "finish";

/** First-class "Describe your project/problem" section copy (spec §4.3 /
 *  §8.3). Promotes the existing embed "Not sure what you need? Describe it
 *  instead →" mode into a real, leadable section. The submitted free text
 *  lands on the EXISTING quote_requests.description column (kept distinct
 *  from `notes` so it stays attributable) — this type only carries the
 *  section's copy, not the value. Every field optional; unset falls back to
 *  in-form defaults (Lane 2 owns the defaults, same pattern as the other
 *  copy fields). Per-form overridable. */
export interface DescribeSectionConfig {
  /** Section/step heading. */
  label?: string;
  /** Optional paragraph rendered under the heading. */
  intro?: string;
  /** Textarea placeholder. */
  placeholder?: string;
  /** Optional helper line under the field. */
  help_text?: string;
  /** Whether a non-empty description is required to submit when this
   *  section leads / is the form's primary intake. Default false. */
  required?: boolean;
}

export interface ShopConfig {
  // Branding
  brand_name: string;
  brand_short: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
  /** Monotonic version counter — the real `shop_config.config_version`
   *  column, bumped on every successful save (see shop-config.ts).
   *  Read-only here: attached by getShopConfig()'s DB read, never
   *  written back through updateShopConfig(). Lets any consumer that
   *  already holds a ShopConfig/PublicShopConfig append it as a
   *  cache-busting query param on the logo-proxy URL, instead of a
   *  fresh DB round-trip — see kickoffs/quote-ui-clarity.md §6. */
  config_version?: number;
  /** Form body/canvas background (fix #2, form-width-and-contrast). Optional
   *  — unset falls back to a light neutral tint (embed + preview both apply
   *  the same default) so an unconfigured shop renders tinted, not white.
   *  Cards, inputs, header, step bar, and footer stay white regardless; this
   *  is only the canvas color behind them. */
  form_bg_color?: string;

  // Form design tokens (Phase 2c / polish-v2). Optional, additive — all four
  // default to the values the renderers hardcode today, so an unset config
  // renders byte-identically. Resolved through src/lib/form-design-tokens.ts
  // (the single source of truth) by all three renderers + the in-app preview.
  // Curated whitelists only — no arbitrary CSS / font URLs.
  // polish-v2: form_size / form_button_radius / form_density are now stored
  // as numbers from the slider controls; legacy string enums ("small",
  // "rounded", "comfortable") are back-compat-mapped at resolve time.
  form_font_family?: string;           // FontFamilyOption — default "system" (Inter stack)
  form_size?: string | number;         // scale 0.85–1.20, default 1.0; legacy: "small"/"medium"/"large"
  form_button_radius?: string | number; // px 0–20, default 9; legacy: "square"/"rounded"/"pill"
  form_density?: string | number;      // multiplier 0.80–1.30, default 1.0; legacy: "compact"/"comfortable"/"spacious"

  // Contact
  support_email: string;
  store_url: string;
  shopify_admin_handle: string;

  /** ISO 4217 code (e.g. "CAD") — the shop's own base/presentment
   *  currency, NOT a customer's foreign presentment currency under
   *  Shopify Markets (out of scope; see kickoffs/shop-currency.md).
   *  Seeded from Shopify's shop.currencyCode by syncShopInfoFromShopify
   *  on install + manual re-sync (src/lib/shopify-shop-info.ts) — never
   *  fetched at render time. Optional so pre-seed / unreachable-Shopify
   *  reads fall through to every formatter's own "USD" default rather
   *  than throwing. */
  currency?: string;

  // App Proxy host (the merchant's myshopify.com subdomain). Lives
  // outside the JSONB blob on the shop_config row's shopify_domain
  // column — exposed on the type so client-side URL builders can
  // prefer <shop>.myshopify.com/a/quote/... over the bare
  // Vercel URL. Null for SW custom_app shops.
  shopify_domain?: string | null;

  // The shop's REAL App Proxy path prefix, captured from Shopify's
  // `path_prefix` query param on a proxied request (lives on
  // shops.proxy_path_prefix, not this JSONB blob). Null/unset means
  // "never captured" — client URL builders fall back to
  // APP_PROXY_PATH_PREFIX ("/a/quote"), which is correct for every
  // shop that hasn't collided with another app's proxy registration.
  // See kickoffs/per-shop-proxy-path.md.
  proxy_path_prefix?: string | null;

  // Business info shown on customer-facing quotes (letterhead-style)
  business_address: string;          // multiline: street / city, state zip
  business_phone: string;

  // Quote terms shown on customer-facing quotes
  default_payment_terms: string;     // legacy single block — kept as fallback when no options exist
  payment_terms_options: PaymentTermsOption[]; // admin-managed list of selectable payment terms; one marked isDefault
  quote_terms_conditions: string;    // multiline T&Cs printed at the bottom of every quote

  /** Cached Shopify shop policies, populated by syncShopPoliciesFromShopify.
   *  Each entry mirrors a Shopify policy with our local `enabled`
   *  toggle. Empty array on shops that haven't synced yet OR shops
   *  whose Shopify policies are all unconfigured (stubs with empty
   *  url/body are filtered out at sync time). The customer quote
   *  page renders enabled entries as a centered row of links below
   *  the free-text T&C block. */
  shopify_policies?: ShopifyPolicy[];

  // Customer-facing form options.
  // facility_types + service_options are LEGACY — kept as the data
  // source for the one-time seed into `custom_fields` on first read.
  // New installs leave both empty and define their own custom_fields.
  facility_types: string[];
  service_options: { id: string; label: string }[];

  // Arbitrary merchant-defined fields on the request form. See CustomField.
  custom_fields: CustomField[];

  // Customer request form (Phase 1 hosted form)
  bundles: Bundle[];                 // pre-built packages shown at top of "Build Your Quote"
  notes_placeholder: string;         // friendly placeholder for the "Notes" field on the form

  // Section toggles. All optional — when absent the form renders the
  // generic defaults. Step count compresses when require_shipping=false.
  // (facility_* / services_section_* are gone — folded into custom_fields.)
  require_shipping?: boolean;            // default: true — false skips Step 2 entirely
  /** ISO-2 codes the request form's Country dropdown offers; a subset of
   *  COUNTRIES (region-data.ts). Resolved via allowedCountries():
   *  unset/empty → US-only owner default (one entry → fixed country, no
   *  dropdown); one entry → fixed country; several → a dropdown of those.
   *  Lazy default (NOT in DEFAULT_SHOP_CONFIG) so the blob stays minimal,
   *  matching consent_* / inventory flags. */
  allowed_countries?: string[];
  // Step labels — drive the progress strip + step headings
  about_you_label?: string;              // default: "About You"
  shipping_label?: string;               // default: "Shipping"
  build_quote_label?: string;            // default: "Build Your Quote"
  finish_up_label?: string;              // default: "Finish Up"
  // Notes section
  notes_label?: string;                  // default: "Notes / anything else"
  // Submit + confirmation
  submit_button_label?: string;          // default: "Submit" (form-polish-v3 #2)
  // Optional note rendered under the Build-step / quick-form subtotal
  // (label + total). Default empty → HIDDEN (form-polish-v3 #5c — the
  // old hardcoded "Final pricing set by your rep" is gone unless a
  // merchant re-adds copy here). Editable in the form copy settings.
  subtotal_note?: string;
  confirmation_headline?: string;        // default uses the SW phrasing in-form
  confirmation_body?: string;            // default: "We'll review your items and be in touch within 1 business day."
  // Confirmation-screen "what happens next" block. Heading + three
  // numbered steps shown after a successful submit on BOTH customer
  // surfaces (embed + hosted /request page). Each independently
  // optional — blank/unset falls back to today's hardcoded copy, so a
  // shop that never touches these renders byte-identically. next_step_2's
  // default interpolates response_time_text; a merchant-set value is
  // rendered verbatim (no token substitution).
  next_steps_heading?: string;           // default: "Here's what happens next"
  next_step_1?: string;                  // default: "A rep reviews your items and verifies pricing + availability."
  next_step_2?: string;                  // default: "We email you a finalized quote within {response time}."
  next_step_3?: string;                  // default: "You review, accept, and we get your order moving."
  response_time_text?: string;           // default: "1 business day"
  trust_signal_text?: string;            // default: "We respond within 1 business day"

  // Optional consent checkbox on step 1 of the customer request form.
  // Off by default — every existing shop renders the form exactly as
  // before until a merchant flips this on in /settings/forms. When
  // enabled + required, the form blocks step transition until the
  // box is ticked; the acceptance is timestamped at submit time on
  // quote_requests.consent_accepted_at.
  consent_enabled?: boolean;             // default false
  consent_required?: boolean;            // default true when enabled
  consent_text?: string;                 // default in CONSENT_DEFAULTS

  // ── Catalog Rules: Hide Price + Hide Add to Cart (v1.1) ────────────
  // Two independent features that share the same shape — 6 fields
  // each, all per-feature. The merchant places a Request Quote
  // button block via the theme editor for customers to act on;
  // neither feature injects a CTA itself (v1.0 did; v1.1 dropped
  // it in favor of theme-side button placement so the storefront
  // CTA renders as part of the merchant's design system, not as
  // an inline-styled DOM patch).
  //
  // Master toggle:  *_enabled
  // Page scope:     all | product_pages_only
  // Vendor target:  any | include <list> | exclude <list>
  // Tag target:     any | include <list> | exclude <list>
  //
  // Vendor + tag filters AND-combine: a product is matched only
  // when BOTH the vendor check AND the tag check pass. Lets a
  // merchant write rules like "Hide price on Hydrogen vendor's
  // products that ALSO carry the 'wholesale' tag".
  //
  // The pure resolver lives in src/lib/storefront-product-matcher.ts
  // — both the embed bundle and admin previews import it so the
  // hide decision is identical client-side and server-side.
  //
  // Defaults intentionally OFF (enabled: false) so every existing
  // merchant renders unchanged until a deliberate flip in the
  // Catalog Rules admin pages.

  // Hide Price
  hide_prices_enabled?: boolean;                                 // default false
  hide_prices_page_scope?: "all" | "product_pages_only";         // default "all"
  hide_prices_vendor_mode?: "any" | "include" | "exclude";       // default "any"
  hide_prices_vendors?: string[];                                // default []
  hide_prices_tag_mode?: "any" | "include" | "exclude";          // default "any"
  hide_prices_tags?: string[];                                   // default []
  /** Combinator when BOTH vendor + tag filters are active.
   *  "any" (default) = vendor match OR tag match → fires.
   *  "all"           = vendor match AND tag match → fires.
   *  Ignored when only one filter is active (nothing to combine).
   *  Defaulted to OR because B2B catalog targeting is usually
   *  additive — "hide for THESE vendors OR products with THESE
   *  tags" matches merchant intent more often than the strict
   *  intersection. The AND opt-in covers the precision case
   *  (e.g. "hide only Hydrogen vendor's wholesale-tagged SKUs"). */
  hide_prices_combinator?: "any" | "all";                        // default "any"

  // Hide Add to Cart — identical structure
  hide_atc_enabled?: boolean;                                    // default false
  hide_atc_page_scope?: "all" | "product_pages_only";            // default "all"
  hide_atc_vendor_mode?: "any" | "include" | "exclude";          // default "any"
  hide_atc_vendors?: string[];                                   // default []
  hide_atc_tag_mode?: "any" | "include" | "exclude";             // default "any"
  hide_atc_tags?: string[];                                      // default []
  hide_atc_combinator?: "any" | "all";                           // default "any"

  // ── Product-page Request-a-Quote button (AUTO-PLACE) ───────────────
  // JS-injected button next to Add-to-Cart on product pages, for
  // themes that can't host the QuoteSense app block inline. OFF by
  // default (auto_place_enabled: false); see ProductButtonConfig.
  product_button?: ProductButtonConfig;

  // ── Quote Basket (P1c) ──────────────────────────────────────────────
  // Standalone storefront basket + "View Quote" edge tab. OFF by default
  // (basket.enabled: false); see BasketConfig. Independent of
  // product_button.auto_place_enabled — the edge tab mounts whenever
  // basket.enabled AND the quote is non-empty, whether the button comes
  // from auto-place or the app block. product_button.mode="add_to_quote"
  // only does something once BOTH a button is present on the page AND
  // basket.enabled.
  basket?: BasketConfig;

  // Defaults
  quote_validity_days: number;
  default_processing_fees: ShopConfigPF[];
  default_vendor_presets: ShopConfigVendorPreset[];

  // GM% color tiers shown on the dashboard, vendor headers, line items,
  // and quote summary. Optional — when absent the gm-color helper
  // falls back to the SW defaults (red ≤ 15, amber ≤ 25, else green).
  // amber_max must be > red_max; both clamped to [0, 100].
  gm_color_thresholds?: {
    red_max: number;
    amber_max: number;
  };

  // Per-field overrides for the form's built-in inputs (first_name,
  // last_name, email, phone, company, preferred_contact, address,
  // city, state, zip). Each field can flip `required` on/off and
  // attach an inline `helper` line shown below the input. Unset
  // keys fall back to the BUILTIN_FIELD_DEFAULTS map in
  // src/lib/builtin-fields.ts — preserving today's behavior on
  // shops that never touch the new admin card.
  builtin_fields?: Partial<Record<BuiltinFieldKey, BuiltinFieldConfig>>;

  /** Per-shop outgoing email envelope config. Controls the from
   *  address's local part, where customer replies route to (rep
   *  directly, a shared inbox, or both), and whether the rep gets
   *  CC'd on every customer-facing send. Defaults are documented on
   *  EmailOutgoingConfig; unset shops behave the way the app shipped
   *  pre-Wave-1 (rep-named from, reply-to-rep, no CC). */
  email_outgoing?: EmailOutgoingConfig;

  /** Subscriptions we've registered with Shopify (orders/paid, etc.).
   *  Operational metadata — not credentials. The webhook signing
   *  secret lives in a dedicated encrypted column on shop_config;
   *  this list just tracks "we've registered these topics" so the
   *  admin UI can show registration status + offer re-register.
   *  Empty / missing => nothing registered yet. */
  shopify_webhooks?: ShopifyWebhookRegistration[];

  /** Customer-side file attachment policy on the request form. When
   *  enabled === false the embed hides the upload UI entirely and
   *  the /api/embed/upload-token endpoint rejects every request.
   *  Limits + allow-list are enforced server-side before issuing a
   *  Blob upload token, so a tampered embed can't bypass them. */
  file_upload?: FileUploadConfig;

  /** Merchant-defined tag library. Reps assign these by id on quotes
   *  + quote_requests; this list is the source of truth for label
   *  + color. Deleting a tag here leaves existing references on
   *  records intact — they render as a neutral "Unknown tag" until
   *  re-assigned. Each id should be a stable slug (e.g. "in-review"). */
  tags?: ShopTag[];

  /** Get-Started widget state — flat `{ <item_key>: true }` map.
   *  Stamped when the merchant clicks "Mark done" on a setup row OR
   *  when a settings page save flips its own key. Items with an
   *  organic completion signal (logo upload, first quote sent) don't
   *  need flags — they compute live. Hidden from PublicShopConfig
   *  (admin-only). Known keys: brand_info_confirmed, colors_confirmed,
   *  terms_confirmed, payment_terms_confirmed, request_form_confirmed,
   *  embed_dismissed. New keys land as the widget adds rows; the type
   *  stays Record<string, boolean> so we don't have to enumerate. */
  setup_progress?: Record<string, boolean>;

  /** Per-step settings — label override, optional intro paragraph,
   *  and (for shipping) the enable toggle. The shipping.enabled key
   *  is the new source of truth for whether the shipping step renders;
   *  legacy `require_shipping` is kept in sync via a dual-write in
   *  resolveShippingEnabled() so older readers + the embed's parallel
   *  inlined helpers don't have to know about form_steps yet. */
  form_steps?: FormStepsConfig;

  // ── Multi-form (docs/MULTI_FORM.md) ─────────────────────────────────────
  /** Named form collection (spec §4.2). Ordered list of composition
   *  override-sets, each layered over this same shop config's SHARED brand
   *  via resolveForm(). The top level of THIS ShopConfig doubles as the
   *  implicit `default` form — so an un-migrated shop with `forms` absent /
   *  empty resolves byte-for-byte as today (back-compat is non-negotiable).
   *  A form entry NEVER carries brand/appearance (see PER_FORM_OVERRIDE_KEYS
   *  for the exact per-form boundary). Kept small (normalizeForms caps at
   *  MAX_FORMS) — this is 2–3 seeded forms, not unlimited authoring (path b
   *  is gated). NOT public: toPublicShopConfig drops it; the public payload
   *  is a single RESOLVED form. */
  forms?: FormDefinition[];

  /** Guided-downgrade selection (tier-gating Lane 2b, spec §6.2) — which
   *  form id survives as THE form once storefront.per_placement_routing
   *  is no longer entitled. Written ONLY by POST /api/billing/downgrade/
   *  prepare, when the merchant explicitly picks one during the guided
   *  downgrade flow at /settings/billing/downgrade. Consumed by
   *  stripConfigForTier: when per_placement_routing is stripped, a
   *  placement's form_id falls back to THIS id (if set) instead of
   *  silently landing on the literal "default" form — otherwise a
   *  merchant who was actually using a named form loses their real form
   *  and nobody chose that outcome. Unset (pre-2b, or a shop that never
   *  ran the guided flow) preserves the old undefined-fallback behavior.
   *  Distinct from `quick_form` (an unrelated, always-available,
   *  ungated single-screen-drawer feature) — this is an id into the
   *  SAME `forms[]` / "default" id space `resolveForm` already uses. */
  starter_form_id?: string | null;

  /** The default form's ordered section list (spec §4.3). Lives at the top
   *  level because the top-level config IS the default form; per-form
   *  overrides carry their own `section_order`. Absent → Lane 2 falls back
   *  to today's hardcoded order (products → contact → shipping → finish), so
   *  an un-migrated shop is unchanged. Validated by normalizeSectionOrder()
   *  (known ids, dedupe, exactly-one-terminal-`finish`-last). */
  section_order?: FormSectionId[];

  /** The default form's Describe-section copy (spec §4.3 / §8.3). Per-form
   *  overridable. See DescribeSectionConfig. */
  describe?: DescribeSectionConfig;

  /** Storefront-drawer quick-submit flow. When enabled (default true)
   *  + a product-page button is configured for quick mode + the drawer
   *  opens with a prefilled product, the embed renders a single-screen
   *  form (name + contact + qty + message) instead of the full 4-step
   *  wizard. The customer can submit immediately or click "+ Add more
   *  products" to expand into the existing full wizard with values
   *  carried forward. See resolveQuickForm() for the defaults the
   *  embed + admin preview both apply on top of partial configs. */
  quick_form?: {
    enabled?: boolean;        // master toggle; default true
    show_phone?: boolean;     // default true
    show_email?: boolean;     // default true
    show_qty?: boolean;       // default true
    show_message?: boolean;   // default true
    submit_label?: string;    // default "Request quote"
    intro?: string;           // optional 1-liner above form
    /** Per-field required toggles. Default false (independently
     *  required) for both — when neither is set, the contact pair
     *  follows the legacy "at least one" rule. When one is set, that
     *  field is hard-required; the other stays optional. When both
     *  are set, both are hard-required. */
    phone_required?: boolean;
    email_required?: boolean;
    /** When true (default), the quick form renders a "+ Add more
     *  products" button that opens an inline picker screen. When
     *  false, the line items list locks to just the prefilled
     *  product — single-product mode. */
    allow_multi_product?: boolean;
    /** Quick form's OWN consent checkbox — independent from the
     *  shared consent_enabled/required/text the full form uses.
     *  Unset falls back to the shared triplet (resolveQuickForm in
     *  form-steps.ts) so a shop that already shows consent on the
     *  quick form today keeps showing it after this field ships. */
    consent_enabled?: boolean;
    consent_required?: boolean;
    consent_text?: string;
    /** Quick form's own accent/button colour. Unset falls back to
     *  the brand accent_color (resolveQuickForm) — no visual change
     *  unless the merchant sets this. */
    accent_color?: string;
    /** Copy for the "switch to the full form" escalation link shown
     *  below Submit (only rendered when the quote has items). Default
     *  "Need shipping or a longer request?" — see resolveQuickForm(). */
    escalation_text?: string;
    /** Show/hide the whole escalation line. Default true. Off for
     *  single-product / quick-only stores where the full multi-step
     *  form doesn't apply — the line made no sense hardcoded on. */
    show_escalation?: boolean;
    /** @deprecated Moved to top-level ShopConfig.builder_url. The
     *  nested copy stays defined for back-compat reads only — the
     *  resolver prefers top-level when both are set, falls back to
     *  this when top-level is unset. Admin writes go to top-level
     *  only; this nested value lingers until the merchant's next
     *  save, at which point it gets overwritten by the wholesale
     *  quick_form blob write (which no longer includes this key). */
    builder_url?: string;
    /** @deprecated Moved to top-level ShopConfig.builder_form_style.
     *  Same back-compat shape as builder_url above. */
    builder_form_style?: "transparent" | "bordered";
    /** The quick form's one optional merchant-configurable binary
     *  (Yes/No) question, rendered beneath the contact fields, above
     *  consent/Submit (kickoff: quick-form-binary-field.md). Absent or
     *  `enabled: false` (the default) ⇒ byte-for-byte unchanged
     *  rendering/submission. See QuickFormCustomField +
     *  normalizeQuickFormCustomField (the nested-key persistence
     *  guard — same bug class as product_button.form_id). */
    custom_field?: QuickFormCustomField;
  };

  /** Which email-shell variant wraps every outbound email. Same
   *  CONTENT (subject lines, body copy, tables) per email type;
   *  only the visual chrome (header band, typography, button shape)
   *  changes. Default 'classic' preserves today's look for every
   *  existing merchant — the dispatcher in email-templates.ts
   *  falls back to classic when this is unset. */
  email_template_style?: "classic" | "minimal" | "brand_heavy" | "modern";

  /** Header chrome for the three banner styles (classic/brand_heavy/modern
   *  — minimal has no header). Default 'initials' preserves today's CSS
   *  brand-initials badge; 'logo' swaps in config.logo_url when it's a
   *  valid absolute https URL, falling back to initials otherwise (see
   *  renderEmailHeaderMark in email-templates/header-mark.ts — the single
   *  place that owns the guard + escaping). Undefined ⇒ 'initials'. */
  email_header_mode?: "initials" | "logo";

  /** Optional merchant copy that personalizes the customer-facing
   *  "your quote is ready" email (sendQuoteToCustomerEmail). Each
   *  field is independently optional; an empty/unset field falls
   *  back to today's default copy, so a shop that never touches this
   *  sends the exact same email as before. See EmailPersonalizationConfig. */
  email_personalization?: EmailPersonalizationConfig;

  /** Optional path on the merchant's store to a dedicated quote
   *  builder page (the inline-embedded full form). e.g.
   *  "/pages/request-a-quote". When set:
   *    - Quick form gets a "Build a larger quote →" link below the
   *      Submit button (deep-links to <builder_url>?import_product=<vid>)
   *    - Inline picker is hidden in quick form (single source of
   *      truth for "more products" flow)
   *    - Cart-page theme block navigates to <builder_url>?import=cart
   *      instead of opening the drawer
   *  Empty/unset = quick form stays self-contained, cart-page block
   *  uses drawer fallback. Validation: must start with "/" if set.
   *  Lives at the top level (not under quick_form) because it's
   *  builder-mode-specific, not a quick-form knob; the legacy nested
   *  copy at quick_form.builder_url is read as a fallback for
   *  back-compat with shops that saved under the old shape. */
  builder_url?: string;
  /** Visual wrapper around the builder-mode form. Default "bordered"
   *  wraps the form in a subtle white card with a light border +
   *  padding so it visually contains itself on plain merchant pages.
   *  "transparent" drops the wrapper for merchants whose pages
   *  already provide their own container styling. Only applies to
   *  data-mode="builder"; wizard / modal / drawer / inline ignore.
   *  Legacy nested copy at quick_form.builder_form_style is read as
   *  a fallback for back-compat. */
  builder_form_style?: "transparent" | "bordered";

  /** Inventory awareness (V1: visibility, V2: reservation).
   *
   *  Shopify tag substring that flags a product as discontinued.
   *  When set, any product whose tags include this string surfaces
   *  a "Discontinued" badge in pickers + line items. Empty string
   *  disables the check entirely (no discontinued evaluation runs).
   *  Default "". Common values: "discontinued", "EOL", "do-not-sell". */
  discontinued_tag?: string;

  /** LEGACY (Wave 1): single switch that blocked BOTH out-of-stock
   *  variants AND discontinued products. Superseded by the split
   *  block_out_of_stock + block_discontinued pair below; kept on
   *  the type for one release so older config blobs with this flag
   *  still resolve correctly via resolveInventoryGates(). Stops
   *  appearing in the admin UI as of the split — new edits write
   *  the two new fields directly. Remove after every live config
   *  has been re-saved at least once (or after the next 90-day
   *  deploy window, whichever lands first). */
  inventory_strict?: boolean;
  /** When true, the storefront embed blocks customers from adding
   *  variants where Shopify says `availableForSale === false` (and
   *  policy === DENY). False (default) lets customers add and rely
   *  on Shopify's checkout-time oversell check. Reps are NEVER
   *  blocked by this flag — backorder is a legitimate rep workflow. */
  block_out_of_stock?: boolean;
  /** When true, BOTH the storefront embed AND the rep product
   *  picker block adding products tagged with `discontinued_tag`.
   *  Reps are blocked here (unlike block_out_of_stock) because
   *  selling discontinued inventory is never legitimate — the
   *  product can't be fulfilled. False (default) lets the
   *  discontinued badge surface without gating add. */
  block_discontinued?: boolean;
}

// ── The shared-vs-per-form boundary (docs/MULTI_FORM.md §4.1) ─────────────
// THE SINGLE SOURCE OF TRUTH for what a form may override. Lane 3's editor,
// the resolver, and the normalizer all agree via this one list. Keys here
// are the COMPOSITION layer (section toggles, field placement, copy); every
// ShopConfig key NOT in this list is SHARED shop-level brand/appearance and
// is never per-form. Add a key here ONLY if it's genuinely per-form
// composition — brand/appearance/policies/payment/business info stay out.
//
// `section_order` and `describe` are ALSO per-form but aren't ShopConfig-
// key overrides of the same shape, so they're modeled separately on
// FormComposition below (and handled explicitly in resolveForm /
// normalizeForms). Everything else per-form is in this list.
export const PER_FORM_OVERRIDE_KEYS = [
  // Section toggles
  "require_shipping",
  "consent_enabled", "consent_required", "consent_text",
  "quick_form",
  // Which built-in / custom fields appear + their step / display_order
  "custom_fields", "builtin_fields",
  // Per-step settings (label / intro / shipping-enabled)
  "form_steps",
  // Copy — step labels
  "about_you_label", "shipping_label", "build_quote_label", "finish_up_label",
  // Copy — notes / submit / subtotal
  "notes_label", "notes_placeholder", "submit_button_label", "subtotal_note",
  // Copy — confirmation + "what happens next" + trust
  "confirmation_headline", "confirmation_body",
  "next_steps_heading", "next_step_1", "next_step_2", "next_step_3",
  "response_time_text", "trust_signal_text",
] as const satisfies readonly (keyof ShopConfig)[];

export type PerFormOverrideKey = (typeof PER_FORM_OVERRIDE_KEYS)[number];

/** The composition layer a form overrides over the shared shop config.
 *  A PARTIAL of the per-form ShopConfig keys, plus the two form-only fields
 *  (`section_order`, `describe`). NOT a full ShopConfig duplicate. */
export type FormComposition = Partial<Pick<ShopConfig, PerFormOverrideKey>> & {
  section_order?: FormSectionId[];
  describe?: DescribeSectionConfig;
};

/** A named form = identity (`id` slug + merchant-facing `name`) + its
 *  composition override-set. Resolved against the shared shop config by
 *  resolveForm(). */
export interface FormDefinition extends FormComposition {
  /** Stable slug, unique within the shop (e.g. "default", "project-intake").
   *  Matches the same shape as a shop slug: ^[a-z0-9][a-z0-9-]*$. */
  id: string;
  /** Merchant-facing name shown in the form list / switcher. */
  name: string;
}

/** Small cap on the forms collection — 2–3 seeded forms is the intended
 *  scale (spec §7). Unlimited from-scratch authoring is gated path b; this
 *  ceiling keeps a malformed/hostile save from bloating the JSONB blob. */
export const MAX_FORMS = 10;

/** Resolved inventory gate flags. Read shop_config through this
 *  helper so the legacy `inventory_strict` single switch falls
 *  through to its split equivalents during the migration window.
 *
 *  Resolution: explicit new-flag values always win. When a new
 *  flag is undefined AND the legacy inventory_strict is true,
 *  treat the new flag as true (preserves Wave 1 behavior for
 *  un-resaved configs). When a new flag is undefined AND legacy
 *  is unset/false → false. Once the merchant saves through the
 *  split admin UI both new flags become explicit booleans and
 *  the legacy field is irrelevant. */
export function resolveInventoryGates(c: ShopConfig | null | undefined): {
  block_out_of_stock: boolean;
  block_discontinued: boolean;
} {
  const legacy = c?.inventory_strict === true;
  const oos = typeof c?.block_out_of_stock === "boolean"
    ? c.block_out_of_stock
    : legacy;
  const disc = typeof c?.block_discontinued === "boolean"
    ? c.block_discontinued
    : legacy;
  return { block_out_of_stock: oos, block_discontinued: disc };
}

/** Where customer replies get routed when they hit Reply / Reply All
 *  on a quote send.
 *
 *  - "rep"    → the rep who sent it (default; matches the original
 *               Wave 1 behavior).
 *  - "shared" → a shared inbox the merchant runs (info@, sales@, etc.).
 *  - "both"   → Reply-To carries BOTH addresses so Reply All hits the
 *               shared inbox + the rep simultaneously.
 *
 *  Stored on shop_config.email_outgoing.reply_to_mode. */
export type ReplyToMode = "rep" | "shared" | "both";

export interface EmailOutgoingConfig {
  /** @deprecated Hardcoded to "quotes" for every shop. Field kept on
   *  the type only so saved data from a previous deploy round-trips
   *  through the admin save layer without TS complaints; the
   *  resolveFromLocalPart() reader ignores whatever's stored here. */
  from_local_part?: string;

  /** Merchant-configured display name for the customer-facing "your
   *  quote is ready" send — used before falling back to brand_name.
   *  OAuth/embedded sessions carry no named per-user identity (Shopify
   *  app-level users), so the resolution chain (real session name →
   *  from_name → brand_name → "Sales") needed a shop-level knob that
   *  doesn't depend on the sending rep having a name at all. Optional;
   *  unset behaves exactly like pre-this-field shops (falls through
   *  the chain). See kickoffs/oauth-email-correctness.md part 2. */
  from_name?: string;

  /** Header band color for the Classic/Brand-heavy email styles. One
   *  merchant knob, not a full email configurator (kickoffs/
   *  email-header-bg-knob.md) — no single default survives every
   *  logo: a light/white logo needs the dark brand band, a dark logo
   *  needs white.
   *   - "auto"  (default/unset) — today's shipped behavior exactly:
   *     white band when the logo is shown, brand primary_color band
   *     for the initials badge.
   *   - "brand" — always primary_color, even with a logo.
   *   - "white" — always white + a subtle border, even with initials.
   *  Resolved (band color + matching on-band text color, together)
   *  by resolveHeaderBand() in header-mark.ts — never re-derive the
   *  pairing ad hoc, or a white band can end up with white text.
   *  Modern/Minimal have no colored band and ignore this entirely. */
  header_bg?: "auto" | "brand" | "white";

  /** Where customer replies route. See ReplyToMode. */
  reply_to_mode?: ReplyToMode;

  /** Required when reply_to_mode is "shared" or "both". Format-checked
   *  email; never echoed back unredacted in customer-visible places. */
  shared_inbox?: string;

  /** When true, the rep is CC'd on every customer-facing email so
   *  they immediately know it went out + get pulled into the thread
   *  on Reply All. Default false — opt-in per shop. */
  cc_rep?: boolean;

  // ── Wave 2: notifications back to the merchant team ──────────────
  // Three triggers fire system-generated emails INTO the merchant's
  // inbox (not to customers). These fields configure who gets pulled
  // in and when. All are optional; defaults preserve the pre-Wave-2
  // behavior (nobody's notified for accept / change-request; new
  // request goes to support_email only).

  /** Email addresses notified when a customer submits a quote request.
   *  Multiple recipients all land on the To: line of a single send.
   *  Empty / unset → falls back to [support_email] so existing shops
   *  keep getting alerts without touching the new setting. */
  new_request_recipients?: string[];

  /** When true, the shared_inbox gets CC'd on the rep-accept
   *  notification. Requires shared_inbox to be set; otherwise a
   *  silent no-op. Default false. */
  notify_team_on_accept?: boolean;

  /** When true, the shared_inbox gets CC'd on the rep-change-request
   *  notification. Same gate as notify_team_on_accept. */
  notify_team_on_changes?: boolean;
}

/** Merchant-editable copy for the customer-facing quote email. A thin
 *  personalization layer over the branded shell: the merchant supplies
 *  a subject line and/or intro/outro paragraphs, optionally with
 *  variable tokens that are resolved server-side at send time.
 *
 *  Supported tokens (case-sensitive): {{customer_name}}, {{quote_number}},
 *  {{quote_url}}, {{brand}}. Resolution happens in email-templates.ts:
 *   - the subject is plain text (tokens substituted raw, CR/LF stripped);
 *   - intro/outro are HTML body context — BOTH the merchant copy AND the
 *     substituted values are HTML-escaped (no raw HTML is ever honored),
 *     and newlines become <br/>.
 *
 *  Every field is independently optional. Empty / unset → the template's
 *  built-in default copy renders, byte-for-byte unchanged. */
export interface EmailPersonalizationConfig {
  /** Subject line for the "your quote is ready" customer email.
   *  Plain-text. Unset/blank → the default subject
   *  ("Quote {ref} from {brand} — {total}"). */
  quote_subject?: string;
  /** Lead paragraph rendered above the quote summary, in place of the
   *  default "Your quote from {brand} is ready." line. The "Hi {name},"
   *  greeting still renders above it. */
  quote_intro?: string;
  /** Closing paragraph rendered below the View-Quote button, in place of
   *  the default "Reply to this email…" line. The "Thanks, {rep}" sign-off
   *  still renders below it. */
  quote_outro?: string;
}

export interface ShopifyWebhookRegistration {
  /** Shopify topic, e.g. "orders/paid". Mirrors Shopify's wire shape. */
  topic: string;
  /** Numeric id returned by Shopify's webhooks API; we keep it to
   *  detect already-registered webhooks before re-creating + to delete
   *  later if the merchant rotates the callback URL. */
  webhook_id: number;
  /** Callback URL we registered. Lets the admin spot stale
   *  registrations (e.g. left over from a preview deploy). */
  address: string;
  /** ISO timestamp at registration time. */
  registered_at: string;
}

export interface FileUploadConfig {
  /** Feature is opt-in per shop — defaults to false until the
   *  merchant turns it on in /settings/forms. */
  enabled: boolean;
  /** Max number of attachments per request. */
  max_files: number;
  /** Per-file size cap in MB (binary, * 1024 * 1024 when comparing). */
  max_file_size_mb: number;
  /** Allow-list. Mix of full MIME types ("application/pdf"), wildcards
   *  ("image/*"), and file extensions starting with "." (".dwg") so
   *  customers can attach engineering files browsers don't always
   *  recognize. Empty array means "no types allowed" — the embed
   *  surfaces a disabled state. */
  allowed_types: string[];
  /** Optional helper line shown under the upload control. */
  helper_text?: string;
}

/** Sensible defaults — covers common B2B attachments (drawings,
 *  specs, RFPs, POs) without inviting random user-supplied junk. The
 *  embed reads this when the shop hasn't customized anything. */
export const DEFAULT_FILE_UPLOAD: FileUploadConfig = {
  enabled: false,
  max_files: 5,
  max_file_size_mb: 10,
  allowed_types: [
    "image/*",
    "application/pdf",
    ".dwg", ".dxf", ".step", ".stp", ".iges", ".igs",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
};

export interface ShopTag {
  id: string;      // slug, unique within the shop
  label: string;   // display name
  color: string;   // hex from TAG_COLORS palette in src/lib/tags.ts
  /** When true (or undefined — legacy default), the tag's LABEL is
   *  pushed to the linked Shopify draft order's `tags` field so reps
   *  can wire Shopify Flow / segments off it. When explicitly false,
   *  the tag is app-internal only. Resolve via
   *  resolveSyncedTagLabels() in src/lib/tags.ts so the default-true
   *  semantics stay in one place. */
  syncToShopify?: boolean;
}

/** Per-step settings (label, intro copy, shipping enable). Each step
 *  shape is optional + omits unset fields, so the saved blob stays
 *  minimal for shops that don't customize anything. */
export interface FormStepsConfig {
  about?:    { label?: string; intro?: string };
  shipping?: { label?: string; intro?: string; enabled?: boolean };
  build?:    { label?: string; search_label?: string; search_placeholder?: string };
  finish?:   {
    label?: string;
    intro?: string;
    notes_label?: string;
    submit_label?: string;
  };
}

export type BuiltinFieldKey =
  | "first_name" | "last_name" | "email" | "phone" | "company"
  | "preferred_contact" | "address" | "street_address_2" | "city" | "country" | "state" | "zip";

export interface BuiltinFieldConfig {
  required?: boolean;
  helper?: string;
  /** Optional label override. Falls back to BUILTIN_FIELD_ROWS' hardcoded label. */
  label?: string;
  /** "about" / "finish" — moves a built-in field out of its hardcoded
   *  section. Falls back to BUILTIN_FIELD_ROWS' default (about for the
   *  personal fields, shipping for address fields). Shipping fields
   *  keep their `shipping` section regardless — the section-level
   *  Require shipping toggle is the only way to hide those. */
  step?: "about" | "shipping" | "finish";
  /** Position within the field's resolved step, sorted ascending.
   *  Defaults to the index from BUILTIN_FIELD_ROWS. */
  display_order?: number;
  /** Per-field width — see CustomField.column_width. Defaults are
   *  resolver-driven (first/last/city/state/zip → half; everything
   *  else → full); a merchant-set value here always wins. */
  column_width?: "full" | "half" | "third";
  /** Conditional visibility — see CustomField.show_when. */
  show_when?: ShowWhenRule;
}

/** Single-condition visibility rule. `field_id` references either a
 *  built-in key (e.g. "phone") or a CustomField.id earlier in the
 *  unified field order. Operators:
 *   - equals/not_equals: scalar comparison (or "contains" semantics
 *     for multiselect dep values — an array `dep` "equals" target X
 *     when it includes X).
 *   - in/not_in: value is an array; matches when the dep's value
 *     intersects it.
 *  See isFieldVisible() in form-field-order.ts. */
export interface ShowWhenRule {
  field_id: string;
  operator: "equals" | "not_equals" | "in" | "not_in";
  value: string | string[];
}

export type PublicShopConfig = Pick<
  ShopConfig,
  "brand_name" | "brand_short" | "logo_url" | "primary_color" | "accent_color"
  // Cache-busting for the logo-proxy URL (kickoffs/quote-ui-clarity.md §6)
  | "config_version"
  // Form body/canvas tint — public so the embed (reads ONLY
  // PublicShopConfig) can resolve it without an authed call.
  | "form_bg_color"
  // Form design tokens — public so the embed (reads ONLY PublicShopConfig)
  // resolves the same appearance as the in-app preview (preview == production).
  | "form_font_family" | "form_size" | "form_button_radius" | "form_density"
  | "support_email" | "store_url" | "facility_types" | "service_options" | "quote_validity_days"
  // The shop's own base currency (ISO 4217). Public so every
  // customer-facing formatter (quote page, print/PDF, email) reads it
  // off the same payload — no authed round-trip just to render a $.
  | "currency"
  | "business_address" | "business_phone" | "default_payment_terms"
  | "payment_terms_options" | "quote_terms_conditions"
  // Shopify-synced policy links — customer quote page renders the
  // enabled entries below the free-text T&C. Public-safe (it's just
  // URLs already on the merchant's public storefront).
  | "shopify_policies"
  | "bundles" | "notes_placeholder"
  | "custom_fields"
  // Form copy + section toggles (all optional)
  | "require_shipping"
  | "allowed_countries"
  | "about_you_label" | "shipping_label" | "build_quote_label" | "finish_up_label"
  | "notes_label"
  | "submit_button_label" | "subtotal_note" | "confirmation_headline" | "confirmation_body"
  | "next_steps_heading" | "next_step_1" | "next_step_2" | "next_step_3"
  | "response_time_text" | "trust_signal_text"
  | "consent_enabled" | "consent_required" | "consent_text"
  // Per-step settings (label/intro/shipping-enabled/notes-label/etc.).
  // Both customer-facing surfaces read this for step copy.
  | "form_steps"
  // Multi-form: the RESOLVED form's ordered section list + Describe copy.
  // Public so Lane 2's config-driven stepDefs + the Describe renderer read
  // them off the single resolved-form payload. `forms` itself stays PRIVATE
  // (authoring collection) — the public payload is one resolved form.
  | "section_order"
  | "describe"
  // Built-in field overrides — embed + hosted form both read these
  // to drive required asterisks + inline helper text.
  | "builtin_fields"
  // Tag library — public so the embed could surface tags later;
  // labels + colors are merchant-curated, not sensitive.
  | "tags"
  // File-upload limits + allow-list — embed must read this to
  // render (or hide) the upload control and to pre-validate before
  // hitting the upload-token endpoint.
  | "file_upload"
  // Quick-form (storefront-drawer single-screen flow). Public so the
  // embed bundle can read it without an authed call.
  | "quick_form"
  // Public-safe so the email-template dispatcher can read it from
  // the PublicShopConfig passed into every send helper. Not
  // sensitive — just visual chrome.
  | "email_template_style"
  // Header chrome (initials badge vs merchant logo) for the same three
  // banner styles — read alongside email_template_style. Miss this and
  // the logo choice never reaches the render helper even though the
  // save succeeded.
  | "email_header_mode"
  // Personalization copy (subject/intro/outro) for the customer quote
  // email. Same rationale as email_template_style: the send helpers +
  // the style preview read it off the PublicShopConfig. Merchant-
  // authored marketing copy — no PII / secrets.
  | "email_personalization"
  // Builder-page settings. Public so the embed (data-mode=builder)
  // can read both without an authed call.
  | "builder_url"
  | "builder_form_style"
  // Inventory awareness. Public so the embed can render badges
  // and enforce strict-mode add/submit gates without authed calls.
  // Legacy inventory_strict stays in the allow-list during the
  // migration window so older bundle caches that still read it
  // keep working; the new split pair is what current code reads.
  | "discontinued_tag"
  | "inventory_strict"
  | "block_out_of_stock"
  | "block_discontinued"
  // Catalog Rules — public because the embed bundle injects CSS
  // on every storefront page load and runs the per-product
  // matcher against window.ShopifyAnalytics.meta.product (vendor
  // + tags) without an authed roundtrip. All 12 fields are
  // merchant-controlled display settings (no PII / secrets).
  | "hide_prices_enabled"
  | "hide_prices_page_scope"
  | "hide_prices_vendor_mode"
  | "hide_prices_vendors"
  | "hide_prices_tag_mode"
  | "hide_prices_tags"
  | "hide_prices_combinator"
  | "hide_atc_enabled"
  | "hide_atc_page_scope"
  | "hide_atc_vendor_mode"
  | "hide_atc_vendors"
  | "hide_atc_tag_mode"
  | "hide_atc_tags"
  | "hide_atc_combinator"
  // Product-page auto-place button. Public because the embed bundle
  // reads it on boot and injects the button on every product page
  // when auto_place_enabled. All fields are merchant-controlled
  // display settings (no PII / secrets).
  | "product_button"
  // Quote Basket. Public because the embed bundle reads it on boot to
  // decide whether to mount the floating indicator + wire the
  // Add-to-Quote sink. Merchant-controlled display settings only.
  | "basket"
> & {
  // App Proxy domain (the merchant's myshopify.com subdomain) when
  // OAuth-installed; null for SW custom_app shops. Not part of the
  // JSONB config blob — lives in the shop_config row's
  // `shopify_domain` column. Surfaced here so client-side URL
  // builders (Share modal copy button, print page CTA) can prefer
  // the proxy URL over the bare Vercel URL.
  shopify_domain?: string | null;
  // The shop's REAL App Proxy path prefix — see the field comment on
  // ShopConfig.proxy_path_prefix. Forwarded alongside shopify_domain
  // so client URL builders never have to re-derive it from a second
  // source of truth. Null/unset -> callers fall back to
  // APP_PROXY_PATH_PREFIX.
  proxy_path_prefix?: string | null;
  // "Powered by QuoteSense" badge visibility (tier-gating Lane 3 §4) —
  // NOT a ShopConfig field (no merchant control over it), computed
  // server-side by the route from the shop's resolved tier/override
  // against the branding.remove_badge entitlement and forwarded here.
  // Read by the customer quote page, the print/PDF view, and the embed's
  // widget footer — all three gate the badge on this ONE flag so there's
  // no tier logic duplicated (or shipped) into the storefront bundle.
  show_powered_by?: boolean;
  // Header band knob for the email renderers — projected out of
  // email_outgoing.header_bg (NOT the whole email_outgoing object,
  // which stays private: shared_inbox / notify recipients are real
  // addresses and this payload also serves the anonymous
  // /api/public/shop-config route + the embed bundle). Same
  // "surface one scalar, not the group" pattern as email_header_mode
  // above. Read via resolveHeaderBand() in header-mark.ts, never raw.
  email_header_bg?: "auto" | "brand" | "white";
};

// Generic, merchant-agnostic defaults. New installs seed this row in
// shop_config; merchants then customize brand, fees, vendor presets,
// payment terms, etc. via /settings/settings. Nothing here references a
// specific store, vendor, or category — all examples sit in the
// merchant's editor as fillable templates, not hard defaults.
export const DEFAULT_SHOP_CONFIG: ShopConfig = {
  brand_name: "",
  brand_short: "",
  logo_url: null,
  primary_color: "#0f172a",
  accent_color: "#2563eb",
  // config_version deliberately absent here — this constant also seeds
  // the JSONB `config` column on a fresh shop's INSERT
  // (updateShopConfig); a literal here would freeze a stale copy inside
  // that blob forever while the real `config_version` column moves on.
  // Callers needing the version for a shop with no row read undefined
  // and treat it as 0 (see getShopConfig's "no row" branch below).
  // Form body/canvas tint — light neutral default (mirrors the embed's
  // FORM_BG_DEFAULT constant in src/embed/index.ts; kept in sync manually,
  // same duplication pattern as primary_color/accent_color's literal
  // fallbacks there).
  form_bg_color: "#f4f5f7",
  // Form design tokens — defaults reproduce today's hardcoded look.
  form_font_family: "system",
  form_size: "medium",
  form_button_radius: "rounded",
  form_density: "comfortable",
  support_email: "",
  store_url: "",
  shopify_admin_handle: "",
  // Regression fence for every existing (pre-multi-currency) shop —
  // unseeded installs stay USD until syncShopInfoFromShopify refreshes
  // them, exactly matching today's hardcoded behavior.
  currency: "USD",
  business_address: "",
  business_phone: "",
  default_payment_terms: "Payment due upon acceptance unless otherwise agreed.",
  payment_terms_options: [
    {
      id: "pt-default",
      label: "Standard",
      body: "Payment due upon acceptance unless otherwise agreed.",
      isDefault: true,
      action_type: "pay_online",
    },
  ],
  quote_terms_conditions: "Pricing is valid through the date shown above and reflects current product availability. Sales tax is calculated based on the ship-to address. By accepting this quote you confirm the products, quantities, and shipping address shown. Returns and warranty terms are governed by the manufacturer.",
  shopify_policies: [],
  facility_types: [],
  service_options: [],
  custom_fields: [],
  // Default the Shipping step ON so the merged config in
  // /api/public/shop-config (which spreads DEFAULT_SHOP_CONFIG ⇒
  // row.config) never has require_shipping land as `undefined` —
  // every consumer (customer page + embed) reads
  // `config.require_shipping !== false`, so `undefined` happened to
  // do the right thing, but having the field explicitly present
  // also makes the toggle save/restore round-trip predictable for
  // the admin editor (the verify-read after save returns a defined
  // boolean instead of a missing property the form has to coalesce).
  require_shipping: true,
  bundles: [],
  notes_placeholder: "Need a W-9? Going out of town and need a specific delivery window? Have a specific PO process at your company? Tell us anything that'll help us serve you better.",
  quote_validity_days: 30,
  default_processing_fees: [
    { id: "pf-cc",  label: "Credit Card", type: "pct", amount: "2.9",  flatAmount: "0" },
    { id: "pf-ach", label: "ACH / Check", type: "pct", amount: "0",    flatAmount: "0" },
  ],
  default_vendor_presets: [],
  // SW's historical thresholds — kept as the seed so unset shops behave
  // identically to today. Merchants can rebalance in /settings/settings.
  gm_color_thresholds: { red_max: 15, amber_max: 25 },
  // Catalog Rules — both features OFF by default + neutral "any"
  // targeting so a fresh install renders an unchanged storefront
  // until the merchant flips the master toggle. Once enabled, the
  // default scope "all" + vendor/tag "any" means the rule applies
  // to every product everywhere; the merchant narrows from there.
  hide_prices_enabled: false,
  hide_prices_page_scope: "all",
  hide_prices_vendor_mode: "any",
  hide_prices_vendors: [],
  hide_prices_tag_mode: "any",
  hide_prices_tags: [],
  hide_prices_combinator: "any",
  hide_atc_enabled: false,
  hide_atc_page_scope: "all",
  hide_atc_vendor_mode: "any",
  hide_atc_vendors: [],
  hide_atc_tag_mode: "any",
  hide_atc_tags: [],
  hide_atc_combinator: "any",
};

// Re-export under the legacy name during the transition so any in-flight
// imports keep working. New code should import DEFAULT_SHOP_CONFIG.
export const SW_DEFAULT_CONFIG = DEFAULT_SHOP_CONFIG;

// Backwards-compat seed: legacy installs only had facility_types and
// service_options. On first read after the schema change we fold them
// into custom_fields so existing data keeps showing on the form.
// Returns the (possibly seeded) list AND a flag so the caller can
// persist once. If custom_fields is already populated, we only
// canonicalize field TYPES in memory — see below.
//
// `changed` means "we seeded new fields, persist them". It deliberately
// stays FALSE for type canonicalization: mapping legacy select/multiselect
// to dropdown/checkbox is a read-path projection, not a migration. Flipping
// it here would make getShopConfig() rewrite every legacy shop's
// shop_config row on its next read (shop-config.ts persists on `changed`),
// which this lane explicitly does not do.
/** Project one `file` field onto its canonical shape, or return null to
 *  DROP it. Same "repair rather than corrupt" contract as normalizeForms:
 *  a bad per-field override falls back to the shop default (the upload is
 *  still governed by resolveUploadRules, which clamps everything), and
 *  only a field with no usable identity — no id, or no label to ask the
 *  question with — is dropped outright. `options` is stripped: a `file`
 *  field has none, and a stale array left behind by a merchant retyping
 *  dropdown → file would otherwise ride along in the JSONB forever.
 *
 *  Read-path only, like its caller: this never reports `changed`, so a
 *  read can never rewrite `shop_config`. */
function normalizeFileField(f: CustomField, type: CustomFieldType): CustomField | null {
  const id = typeof f.id === "string" ? f.id.trim() : "";
  const label = typeof f.label === "string" ? f.label.trim() : "";
  if (!id || !label) return null;

  const out: CustomField = { ...f, id, label, type };
  delete out.options;

  const posInt = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : undefined;
  const maxFiles = posInt(f.max_files);
  const maxSize = posInt(f.max_file_size_mb);
  const allowed = Array.isArray(f.allowed_types)
    ? f.allowed_types.filter(t => typeof t === "string" && t.trim().length > 0)
    : undefined;

  if (maxFiles === undefined) delete out.max_files; else out.max_files = maxFiles;
  if (maxSize === undefined) delete out.max_file_size_mb; else out.max_file_size_mb = maxSize;
  if (!allowed || allowed.length === 0) delete out.allowed_types; else out.allowed_types = allowed;

  return out;
}

export function normalizeCustomFields(
  custom: CustomField[] | undefined,
  legacyFacilityTypes: string[] | undefined,
  legacyServiceOptions: { id: string; label: string }[] | undefined,
): { fields: CustomField[]; changed: boolean } {
  if (Array.isArray(custom) && custom.length > 0) {
    // Project legacy type names onto canonical ones so everything
    // downstream (three renderers, two validators, the display resolver)
    // only ever sees "dropdown" | "radio" | "checkbox" | "text" | "textarea".
    // An unknown type is DROPPED rather than guessed — rendering nothing is
    // recoverable; rendering the wrong control silently corrupts an answer.
    const fields: CustomField[] = [];
    for (const f of custom) {
      if (!f || typeof f !== "object") continue;
      const type = canonicalCustomFieldType(f.type);
      if (!type) continue;
      if (type === "file") {
        const normalized = normalizeFileField(f, type);
        if (normalized) fields.push(normalized);
        continue;
      }
      fields.push(type === f.type ? f : { ...f, type });
    }
    return { fields, changed: false };
  }
  const seeded: CustomField[] = [];
  if (Array.isArray(legacyFacilityTypes) && legacyFacilityTypes.length > 0) {
    seeded.push({
      id: "facility_type",
      label: "Facility type",
      type: "dropdown",
      step: "about",
      required: false,
      options: legacyFacilityTypes.map(v => ({
        id: v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || `opt-${Math.random().toString(36).slice(2, 6)}`,
        label: v,
      })),
    });
  }
  if (Array.isArray(legacyServiceOptions) && legacyServiceOptions.length > 0) {
    seeded.push({
      id: "services",
      label: "Services",
      type: "checkbox",
      step: "finish",
      required: false,
      options: legacyServiceOptions.map(o => ({ id: o.id, label: o.label })),
    });
  }
  return { fields: seeded, changed: seeded.length > 0 };
}

// Backwards-compat: older rows have payment_terms_options entries without
// action_type. Default to "pay_online" so existing behavior carries over.
// Returns the normalized list AND a flag for whether anything changed —
// the caller persists when changed === true so we only write once.
export function normalizePaymentTermsOptions(
  opts: PaymentTermsOption[] | undefined,
): { options: PaymentTermsOption[]; changed: boolean } {
  if (!Array.isArray(opts) || opts.length === 0) {
    return { options: opts || [], changed: false };
  }
  let changed = false;
  const out = opts.map(o => {
    if (!o.action_type) {
      changed = true;
      return { ...o, action_type: "pay_online" as const };
    }
    return o;
  });
  return { options: out, changed };
}

// ── Multi-form resolver + normalizers (docs/MULTI_FORM.md §4.2 / §6) ───────

/** Resolve the EFFECTIVE config for a form = the shared shop config with the
 *  matching form's composition overrides layered on top. Signature that
 *  Lanes 2/3 build against:
 *
 *      resolveForm(config: ShopConfig, formId?: string): ShopConfig
 *
 *  Feed the result to toPublicShopConfig() to get the public payload for a
 *  single form.
 *
 *  BACK-COMPAT (non-negotiable): `formId` unset / "default" / unknown, OR a
 *  shop with no `forms` collection → returns the stored config AS-IS (the
 *  same reference), which is the implicit `default` form. So a shop that
 *  never adopted multi-form resolves byte-for-byte as today.
 *
 *  Only PER_FORM_OVERRIDE_KEYS (+ section_order + describe) are layered —
 *  the resolver filters to the documented boundary, so a form can NEVER
 *  override shared brand/appearance even if a malformed entry smuggled such
 *  a key past the normalizer (defense in depth). */
export function resolveForm(config: ShopConfig, formId?: string): ShopConfig {
  const id = typeof formId === "string" ? formId.trim().toLowerCase() : "";
  if (!id || id === "default") return config;
  const forms = Array.isArray(config.forms) ? config.forms : [];
  const match = forms.find((f) => f && f.id === id);
  if (!match) return config; // unknown id → default form (back-compat)
  const resolved: ShopConfig = { ...config };
  for (const k of PER_FORM_OVERRIDE_KEYS) {
    if (k in match && (match as any)[k] !== undefined) {
      (resolved as any)[k] = (match as any)[k];
    }
  }
  if (match.section_order !== undefined) resolved.section_order = match.section_order;
  if (match.describe !== undefined) resolved.describe = match.describe;
  return resolved;
}

const FORM_SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

/** Sanitize an incoming `section_order` into a valid ordered list, or
 *  `undefined` when it can't be made valid (so a bad value is DROPPED, never
 *  corrupts config). Guarantees: only known ids (FORM_SECTION_IDS), deduped
 *  (first occurrence wins), and exactly one terminal `finish` appended LAST.
 *  `products` may be omitted; `describe`/`contact` may lead. Requires at
 *  least one non-terminal section — an order of only `finish` (or all-junk)
 *  resolves to undefined. */
export function normalizeSectionOrder(raw: unknown): FormSectionId[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const seen = new Set<string>();
  const out: FormSectionId[] = [];
  for (const s of raw) {
    if (typeof s !== "string") continue;
    const id = s.trim().toLowerCase();
    if (!FORM_SECTION_IDS.includes(id as FormSectionId)) continue;
    if (id === FORM_SECTION_TERMINAL) continue; // appended once below
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id as FormSectionId);
  }
  if (out.length === 0) return undefined;
  out.push(FORM_SECTION_TERMINAL);
  return out;
}

/** Sanitize/validate an incoming `forms` payload before it reaches the JSONB
 *  column (spec §6). A malformed save must NOT corrupt config, so every rule
 *  DROPS or REPAIRS rather than throws:
 *    - non-array → `[]`
 *    - drop non-object entries and entries with a missing/invalid `id`
 *      (must match ^[a-z0-9][a-z0-9-]*$)
 *    - enforce UNIQUE ids (later duplicates dropped)
 *    - `name` coerced to a non-empty string, defaulting to the id
 *    - keep ONLY per-form keys (PER_FORM_OVERRIDE_KEYS + section_order +
 *      describe) — shared brand/appearance keys are STRIPPED (the boundary
 *      is enforced on write, not just at resolve time)
 *    - `section_order` run through normalizeSectionOrder (dropped if invalid)
 *    - `describe` kept only when a plain object
 *    - cap the collection at MAX_FORMS
 *  Returns the cleaned list + a `changed` flag (family parity with
 *  normalizeCustomFields / normalizePaymentTermsOptions). */
export function normalizeForms(raw: unknown): { forms: FormDefinition[]; changed: boolean } {
  if (raw === undefined) return { forms: [], changed: false };
  if (!Array.isArray(raw)) return { forms: [], changed: true };
  const keptKeys = new Set<string>([
    "id", "name", "section_order", "describe", ...PER_FORM_OVERRIDE_KEYS,
  ]);
  const out: FormDefinition[] = [];
  const seenIds = new Set<string>();
  let changed = false;
  for (const entry of raw) {
    if (out.length >= MAX_FORMS) { changed = true; break; }
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) { changed = true; continue; }
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === "string" ? e.id.trim().toLowerCase() : "";
    if (!FORM_SLUG_RE.test(id)) { changed = true; continue; }
    if (seenIds.has(id)) { changed = true; continue; }
    seenIds.add(id);
    const name = typeof e.name === "string" && e.name.trim() ? e.name.trim() : id;
    if (name !== e.name) changed = true;
    const form: FormDefinition = { id, name };
    for (const k of PER_FORM_OVERRIDE_KEYS) {
      if (k in e && e[k] !== undefined) (form as any)[k] = e[k];
    }
    if ("section_order" in e) {
      const so = normalizeSectionOrder(e.section_order);
      if (so) form.section_order = so; else changed = true;
    }
    if ("describe" in e) {
      if (e.describe && typeof e.describe === "object" && !Array.isArray(e.describe)) {
        form.describe = e.describe as DescribeSectionConfig;
      } else changed = true;
    }
    // Flag (for callers that want to persist-once) when the entry carried
    // keys we stripped — shared brand keys or unknown junk.
    if (Object.keys(e).some((k) => !keptKeys.has(k))) changed = true;
    out.push(form);
  }
  return { forms: out, changed };
}

/** Sanitize `product_button.form_id` before it reaches the JSONB column
 *  (kickoff: forms-nav-and-autoplace-form.md §2 — this exact bug class has
 *  silently reverted merchant saves five times, so a new key gets a
 *  normalizer + regression test up front). Every other product_button
 *  field rides through untouched (see toPublicShopConfig / getShopConfig)
 *  — only form_id needs shape validation now that it's set via a real
 *  dropdown rather than free text. Unset/blank stays unset (default form,
 *  unchanged behavior). A malformed value (wrong type, blank after trim,
 *  or not slug-shaped) is DROPPED rather than persisted, same "repair
 *  rather than corrupt" contract as normalizeForms. Doesn't need the live
 *  forms list to do its job — resolveForm() already falls back to the
 *  default form for an id that matches no forms[] entry (e.g. the picked
 *  form was later deleted). */
export function normalizeProductButton(
  raw: ProductButtonConfig | undefined,
): { config: ProductButtonConfig | undefined; changed: boolean } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { config: raw, changed: false };
  if (!("form_id" in raw) || raw.form_id === undefined) return { config: raw, changed: false };
  const v: unknown = (raw as any).form_id;
  if (typeof v !== "string") {
    const { form_id: _drop, ...rest } = raw;
    return { config: rest, changed: true };
  }
  const id = v.trim().toLowerCase();
  if (!id || !FORM_SLUG_RE.test(id)) {
    const { form_id: _drop, ...rest } = raw;
    return { config: rest, changed: true };
  }
  return id === v ? { config: raw, changed: false } : { config: { ...raw, form_id: id }, changed: true };
}

/** Sanitize `quick_form.custom_field` before it reaches the JSONB column
 *  (kickoff: quick-form-binary-field.md §2 — the same nested-key
 *  persistence trap as `product_button.form_id` above: `quick_form` is
 *  itself on both PATCH allow-lists, but an allow-listed PARENT key
 *  doesn't validate a NESTED one — this exact bug class silently
 *  reverted merchant saves six times). Validates the id slug shape + a
 *  non-empty label, trims/defaults true_label/false_label to "Yes"/"No",
 *  and coerces enabled/required to booleans. A malformed custom_field
 *  (missing/invalid id, blank label, wrong type) is DROPPED — the rest
 *  of `quick_form` still persists untouched, same "repair rather than
 *  corrupt" contract as normalizeProductButton / normalizeForms. */
export function normalizeQuickForm(
  raw: ShopConfig["quick_form"] | undefined,
): { config: ShopConfig["quick_form"] | undefined; changed: boolean } {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { config: raw, changed: false };
  if (!("custom_field" in raw) || raw.custom_field === undefined) return { config: raw, changed: false };
  const cf = raw.custom_field as unknown;
  if (!cf || typeof cf !== "object" || Array.isArray(cf)) {
    const { custom_field: _drop, ...rest } = raw;
    return { config: rest, changed: true };
  }
  const c = cf as QuickFormCustomField;
  const id = typeof c.id === "string" ? c.id.trim().toLowerCase() : "";
  const label = typeof c.label === "string" ? c.label.trim() : "";
  if (!id || !FORM_SLUG_RE.test(id) || !label) {
    const { custom_field: _drop, ...rest } = raw;
    return { config: rest, changed: true };
  }
  const trueLabel = (typeof c.true_label === "string" && c.true_label.trim()) ? c.true_label.trim() : "Yes";
  const falseLabel = (typeof c.false_label === "string" && c.false_label.trim()) ? c.false_label.trim() : "No";
  const helpText = (typeof c.help_text === "string" && c.help_text.trim()) ? c.help_text.trim() : undefined;
  const enabled = c.enabled === true;
  const required = c.required === true;
  const normalized: QuickFormCustomField = {
    enabled, id, label, required,
    true_label: trueLabel, false_label: falseLabel,
    ...(helpText ? { help_text: helpText } : {}),
  };
  const changed = id !== c.id || label !== c.label
    || enabled !== c.enabled || required !== c.required
    || trueLabel !== c.true_label || falseLabel !== c.false_label
    || helpText !== c.help_text;
  return { config: { ...raw, custom_field: normalized }, changed };
}

export function toPublicShopConfig(
  c: ShopConfig,
  shopify_domain?: string | null,
  install_type?: "oauth" | "custom_app" | null,
  show_powered_by?: boolean,
  proxy_path_prefix?: string | null,
): PublicShopConfig {
  // App Store rule 1.1.2 — OAuth shops must not surface payment
  // affordances that bypass Shopify checkout. The external-payment-
  // link feature stays available for custom_app installs (SW is
  // grandfathered). Strip per-term link fields from the public
  // payload so the customer-facing renderer cannot draw a button
  // for an OAuth tenant even if one is somehow still in the row.
  const rawPaymentTerms = c.payment_terms_options || [];
  const sanitizedPaymentTerms = install_type === "oauth"
    ? rawPaymentTerms.map(({ payment_link_url: _u, payment_link_label: _l, ...rest }) => rest)
    : rawPaymentTerms;
  return {
    brand_name: c.brand_name,
    brand_short: c.brand_short,
    logo_url: c.logo_url,
    primary_color: c.primary_color,
    accent_color: c.accent_color,
    config_version: c.config_version ?? 0,
    // Forwarded as-is (unset stays undefined so the embed applies its own
    // light-neutral default — same "forward raw, let the reader default"
    // pattern as the form design tokens right below).
    form_bg_color: c.form_bg_color,
    // Form design tokens — forwarded as-is (unset stays undefined so the
    // resolver applies its byte-identical defaults).
    form_font_family: c.form_font_family,
    form_size: c.form_size,
    form_button_radius: c.form_button_radius,
    form_density: c.form_density,
    support_email: c.support_email,
    store_url: c.store_url,
    // Fallback applied HERE, at the one boundary every customer-facing
    // render path reads through — an unseeded/pre-migration config row
    // (currency undefined in the DB) still resolves to "USD", matching
    // today's hardcoded behavior exactly.
    currency: c.currency || "USD",
    facility_types: c.facility_types,
    service_options: c.service_options,
    quote_validity_days: c.quote_validity_days,
    business_address: c.business_address,
    business_phone: c.business_phone,
    default_payment_terms: c.default_payment_terms,
    payment_terms_options: sanitizedPaymentTerms,
    quote_terms_conditions: c.quote_terms_conditions,
    shopify_policies: c.shopify_policies || [],
    bundles: c.bundles || [],
    notes_placeholder: c.notes_placeholder || "",
    custom_fields: c.custom_fields || [],
    // Forward only the keys that are actually set. Form code falls back
    // to in-line defaults when a field is undefined, so propagating the
    // raw value here is correct — don't paper over `undefined`.
    require_shipping: c.require_shipping,
    // ISO-2 allow-list for the request form's Country dropdown. Forwarded
    // so the embed (which reads ONLY PublicShopConfig) can resolve it.
    allowed_countries: c.allowed_countries,
    about_you_label: c.about_you_label,
    shipping_label: c.shipping_label,
    build_quote_label: c.build_quote_label,
    finish_up_label: c.finish_up_label,
    notes_label: c.notes_label,
    submit_button_label: c.submit_button_label,
    subtotal_note: c.subtotal_note,
    confirmation_headline: c.confirmation_headline,
    confirmation_body: c.confirmation_body,
    next_steps_heading: c.next_steps_heading,
    next_step_1: c.next_step_1,
    next_step_2: c.next_step_2,
    next_step_3: c.next_step_3,
    response_time_text: c.response_time_text,
    trust_signal_text: c.trust_signal_text,
    // Consent fields — both customer-facing form surfaces (embed +
    // hosted /request page) read these to decide whether to render
    // the checkbox + whether to block submission when unchecked.
    consent_enabled: c.consent_enabled,
    consent_required: c.consent_required,
    consent_text: c.consent_text,
    form_steps: c.form_steps,
    // Multi-form — forwarded as-is (unset stays undefined so Lane 2 applies
    // its own default order / describe copy). `forms` is intentionally NOT
    // forwarded: the resolved single form is what the embed reads.
    section_order: c.section_order,
    describe: c.describe,
    builtin_fields: c.builtin_fields,
    tags: c.tags,
    file_upload: c.file_upload,
    quick_form: c.quick_form,
    email_template_style: c.email_template_style,
    email_header_mode: c.email_header_mode,
    email_personalization: c.email_personalization,
    // Narrow projection of email_outgoing.header_bg — see the field
    // comment on PublicShopConfig for why the rest of email_outgoing
    // never rides along on this public payload.
    email_header_bg: c.email_outgoing?.header_bg,
    // Top-level builder fields — preferred. The legacy nested
    // copy at c.quick_form.builder_* still rides through inside
    // c.quick_form (above) so an unmigrated cache hit can fall
    // back to it. The form-steps resolver + embed both prefer
    // top-level when both are present.
    builder_url: c.builder_url,
    builder_form_style: c.builder_form_style,
    // Inventory awareness — embed needs the discontinued tag for
    // badge rendering + the two gate booleans for add/submit blocks.
    // Resolved through the legacy fallback so configs that still
    // carry only inventory_strict (older shops, pre-split) project
    // out as { block_out_of_stock: true, block_discontinued: true }
    // verbatim. Legacy inventory_strict ALSO rides through for one
    // release so a stale cached embed bundle that reads it instead
    // of the split pair keeps working.
    discontinued_tag: c.discontinued_tag,
    inventory_strict: c.inventory_strict,
    block_out_of_stock: resolveInventoryGates(c).block_out_of_stock,
    block_discontinued: resolveInventoryGates(c).block_discontinued,
    // Catalog Rules — embed reads these on boot, runs the
    // matcher against window.ShopifyAnalytics.meta.product, then
    // sets data-qs-hide-{price,atc} attributes on <html> /
    // per-product cards so CSS rules apply at the right scope.
    // Forward as-is so unset fields stay undefined and the embed
    // can resolve its own defaults (every *_enabled lookup falls
    // through to false on undefined).
    hide_prices_enabled:     c.hide_prices_enabled,
    hide_prices_page_scope:  c.hide_prices_page_scope,
    hide_prices_vendor_mode: c.hide_prices_vendor_mode,
    hide_prices_vendors:     c.hide_prices_vendors,
    hide_prices_tag_mode:    c.hide_prices_tag_mode,
    hide_prices_tags:        c.hide_prices_tags,
    hide_prices_combinator:  c.hide_prices_combinator,
    hide_atc_enabled:        c.hide_atc_enabled,
    hide_atc_page_scope:     c.hide_atc_page_scope,
    hide_atc_vendor_mode:    c.hide_atc_vendor_mode,
    hide_atc_vendors:        c.hide_atc_vendors,
    hide_atc_tag_mode:       c.hide_atc_tag_mode,
    hide_atc_tags:           c.hide_atc_tags,
    hide_atc_combinator:     c.hide_atc_combinator,
    // Product-page auto-place button — forwarded as-is so unset
    // fields stay undefined and the embed's resolveProductButtonConfig
    // applies its own defaults. Gated behind auto_place_enabled, so an
    // unset block is a full no-op on every storefront.
    product_button:          c.product_button,
    // Quote Basket — forwarded as-is so unset stays undefined and the
    // embed's resolveBasketConfig applies its own defaults. Gated
    // behind basket.enabled, so an unset block is a full no-op.
    basket:                  c.basket,
    // App Proxy domain — null/undefined for SW custom_app. When set,
    // client URL builders prefer <shop>.myshopify.com/a/quote/...
    // over the bare Vercel URL so reps copying share links get the
    // native customer experience.
    shopify_domain: shopify_domain ?? null,
    // The shop's real App Proxy path prefix (see the field comment on
    // PublicShopConfig above). Null when the caller doesn't pass one —
    // every builder's own default (APP_PROXY_PATH_PREFIX) applies.
    proxy_path_prefix: proxy_path_prefix ?? null,
    // Defaults false (badge hidden) when the caller doesn't pass it —
    // matches the route's fail-safe default on a billing-resolver error.
    show_powered_by: show_powered_by === true,
  };
}

// Default copy for the consent checkbox. Surfaced when consent_enabled
// is true and the merchant hasn't customized the text. Plain string —
// the form renders it as plain text (HTML escaped) so a paste of odd
// characters doesn't break the layout.
export const CONSENT_DEFAULT_TEXT = "I agree to be contacted about my quote request.";
